/**
 * Device and browser fingerprinting utilities
 */

export interface DeviceInfo {
  deviceType: 'mobile' | 'tablet' | 'desktop';
  browser: string;
  browserVersion: string;
  os: string;
  language: string;
  timezone: string;
  screenResolution: string;
  screenWidth: number;
  screenHeight: number;
}

export class Fingerprint {
  
  /**
   * Get complete device information
   */
  static getDeviceInfo(): DeviceInfo {
    return {
      deviceType: this.getDeviceType(),
      browser: this.getBrowser(),
      browserVersion: this.getBrowserVersion(),
      os: this.getOS(),
      language: this.getLanguage(),
      timezone: this.getTimezone(),
      screenResolution: this.getScreenResolution(),
      screenWidth: window.screen?.width || 0,
      screenHeight: window.screen?.height || 0
    };
  }

  /**
   * Detect device type
   */
  static getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const ua = navigator.userAgent.toLowerCase();
    
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    
    if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
      return 'mobile';
    }
    
    return 'desktop';
  }

  /**
   * Detect browser name
   */
  static getBrowser(): string {
    const ua = navigator.userAgent.toLowerCase();
    
    if (ua.includes('edg/')) return 'Edge';
    if (ua.includes('chrome/') && !ua.includes('edg/')) return 'Chrome';
    if (ua.includes('safari/') && !ua.includes('chrome/')) return 'Safari';
    if (ua.includes('firefox/')) return 'Firefox';
    if (ua.includes('opera/') || ua.includes('opr/')) return 'Opera';
    if (ua.includes('trident/') || ua.includes('msie')) return 'IE';
    
    return 'Unknown';
  }

  /**
   * Get browser version
   */
  static getBrowserVersion(): string {
    const ua = navigator.userAgent;
    let match;
    
    if ((match = ua.match(/(?:edg|edge)\/(\d+)/i))) return match[1];
    if ((match = ua.match(/chrome\/(\d+)/i))) return match[1];
    if ((match = ua.match(/firefox\/(\d+)/i))) return match[1];
    if ((match = ua.match(/version\/(\d+)/i))) return match[1]; // Safari
    if ((match = ua.match(/opera\/(\d+)/i))) return match[1];
    if ((match = ua.match(/msie (\d+)/i))) return match[1];
    
    return 'Unknown';
  }

  /**
   * Detect operating system
   */
  static getOS(): string {
    const ua = navigator.userAgent.toLowerCase();
    
    if (ua.includes('win')) return 'Windows';
    if (ua.includes('mac')) return 'macOS';
    if (ua.includes('linux')) return 'Linux';
    if (ua.includes('android')) return 'Android';
    if (ua.includes('iphone') || ua.includes('ipad')) return 'iOS';
    
    return 'Unknown';
  }

  /**
   * Get browser language
   */
  static getLanguage(): string {
    return navigator.language || (navigator as any).userLanguage || 'en';
  }

  /**
   * Get timezone
   */
  static getTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      return 'UTC';
    }
  }

  /**
   * Get screen resolution
   */
  static getScreenResolution(): string {
    return `${window.screen.width}x${window.screen.height}`;
  }

  /**
   * Generate unique session ID
   */
  static generateSessionId(): string {
    return `session_${Date.now()}_${this.randomString(8)}`;
  }

  /**
   * Generate unique event ID
   */
  static generateEventId(): string {
    return `event_${Date.now()}_${this.randomString(12)}`;
  }

  /**
   * Generate random alphanumeric string
   */
  private static randomString(length: number): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Check if Do Not Track is enabled
   */
  static isDoNotTrackEnabled(): boolean {
    const dnt = (navigator as any).doNotTrack || 
                (window as any).doNotTrack || 
                (navigator as any).msDoNotTrack;
    
    return dnt === '1' || dnt === 'yes';
  }
}
